import { useCallback, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';

import { DEFAULT_INPUT_NAME } from 'hb-react/shared/components/Turnstile/constants';
import { TurnstileWidget } from 'hb-react/shared/components/Turnstile/TurnstileWidget';

export interface TurnstileInputProps {
  name?: string;
  onError?: (error: string) => void;
  onExpire?: () => void;
  onSuccess?: (token: string) => void;
}

/**
 * A component that wraps the TurnstileWidget and includes a hidden input to store the token.
 * If you have a Formik form, prefer using the TurnstileFormikField component.
 *
 * All props are optional. `name` will default to the value of DEFAULT_INPUT_NAME and
 * the callbacks are there for convenience only as the token will be stored in the hidden input internally
 * and turnstile will retry the challenge automatically. on its own.
 *
 * @param name The name of the hidden input that will store the token.
 * @param onSuccess A callback that will be called when the Turnstile challenge is successfully resolved.
 * @param onError A callback that will be called when an error occurs during the Turnstile challenge.
 * @param onExpire A callback that will be called when the Turnstile challenge expires.
 */
export const TurnstileInput = ({
  name = DEFAULT_INPUT_NAME,
  onSuccess,
  onError,
  onExpire,
}: TurnstileInputProps) => {
  const [token, setToken] = useState('');

  const handleSuccess = useCallback(
    (value: string) => {
      setToken(value);

      onSuccess?.(value);
    },
    [onSuccess],
  );

  return (
    <Box data-testid="turnstile-container">
      <TurnstileWidget
        onSuccess={handleSuccess}
        onError={onError}
        onExpire={onExpire}
      />
      <input
        type="hidden"
        name={name}
        required
        value={token}
        data-testid={name}
      />
    </Box>
  );
};
